import { isEmpty } from 'lodash'
import { BaseTheme } from 'opensolar-ui'
import { HardwareSupplierFilterKeyType, SUPPLIER_INTEGRATION_TYPES } from 'opensolar-common'
import { STOCK_LEVEL_STATUS } from 'pages/ordering/constants'
import { getSupplierConfigNonReact } from 'pages/ordering/hooks/useGetSupplierConfig'
import { numberWithCommas } from 'util/misc'
import { DistributorDataTypeV2 } from '../types'
import { AVAILABLE_STATUSES, StockStatusMessagesType } from './stockUtils'

export const GREEN = BaseTheme.palette.green[600]
export const RED = BaseTheme.palette.red[800]

const PRICE_ON_APPLICATION_INTEGRATION_TYPES = new Set([
  SUPPLIER_INTEGRATION_TYPES.PRICE_ON_APPLICATION,
  SUPPLIER_INTEGRATION_TYPES.PRICE_ON_APPLICATION_WITH_TIERS,
])

const isPriceOnApplicationSupplier = (source: string | undefined): boolean => {
  if (!source || source === 'component') {
    return false
  }

  const integrationType = getSupplierConfigNonReact(source as HardwareSupplierFilterKeyType)?.integrationType
  return integrationType != null && PRICE_ON_APPLICATION_INTEGRATION_TYPES.has(integrationType)
}

const getPriceOnApplicationStockTextAndColor = (
  distributors: DistributorDataTypeV2[],
  source: string
): { stockText: string; stockTextColor: string } | null => {
  const distributorRow = distributors.find((row) => row.distributor === source)
  if (!distributorRow) {
    return null
  }

  if (distributorRow.is_in_stock === false) {
    return { stockText: 'Out of stock', stockTextColor: RED }
  }

  return { stockText: 'In stock', stockTextColor: GREEN }
}

export function getStockTextAndColor(
  stockStatusPerDistributor: StockStatusMessagesType[],
  quantityAvailable: number,
  source: string | undefined,
  distributors: DistributorDataTypeV2[] = []
) {
  if (source && isPriceOnApplicationSupplier(source)) {
    const poaStockTextAndColor = getPriceOnApplicationStockTextAndColor(distributors, source)
    if (poaStockTextAndColor) {
      return poaStockTextAndColor
    }
  }

  // Handle outlet case
  if (stockStatusPerDistributor.length === 1 && stockStatusPerDistributor[0].distributor === 'outlet') {
    let stockText = 'In stock'
    let stockTextColor = GREEN

    if (stockStatusPerDistributor[0].status === STOCK_LEVEL_STATUS.NOT_AVAILABLE) {
      stockText = 'Out of stock'
      stockTextColor = RED
    } else if (stockStatusPerDistributor[0].status === STOCK_LEVEL_STATUS.AVAILABLE_FOR_PRE_ORDER) {
      stockText = 'Pre-order available'
      stockTextColor = GREEN
    }

    return { stockText, stockTextColor }
  }

  const stockText =
    quantityAvailable === 0
      ? 'Out of stock'
      : source
        ? `${numberWithCommas(quantityAvailable, true)} in stock`
        : 'In stock'

  const stockTextColor =
    !isEmpty(stockStatusPerDistributor) && AVAILABLE_STATUSES.includes(stockStatusPerDistributor[0].status)
      ? GREEN
      : RED

  return { stockText, stockTextColor }
}
